import React, { useCallback, useMemo, useState } from 'react';
import type {
  KanbanBoard,
  KanbanCard,
  KanbanColumn,
} from '../../DesignSystems/Kanban';
import Kanban from '../../DesignSystems/Kanban';
import { StylishNewButton } from '../../DesignSystems/New/StylishNewButton';
import { useIncidentResourceRequirements } from '../hooks/incidentResourceRequirementHooks';
import { useResourceSets } from '../hooks/resourceSetHooks';
import { useResourceSites } from '../hooks/resourceSiteHooks';
import {
  useIncidentResourceRequests,
  useUpdateResourceRequest,
} from '../hooks/incidentResourceRequestHooks';
import Loading from '../../Loading/Loading';
import ResourceRequestCreateDrawer from './ResourceRequestCreateDrawer';
import ResourceRequestKanbanCard from './components/ResourceRequestKanbanCard';
import ResourceRequestKanbanFilters from './components/ResourceRequestKanbanFilters';
import {
  calculateAggregatedNeeds,
  calculateAvailableResourceKinds,
  createRequestCard,
  filterRequestsByKind,
} from './utils/KanbanHelpers';
import { SharedIcon } from '../../SharedIcon/SharedIcon';

import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import Sentry from '../../../instrument';

interface ResourceRequestsKanbanProps {
  onViewDetails?: (request: any) => void;
}

// Column IDs for the kanban board
const COLUMN_IDS = {
  CANCELLED: 'cancelled',
  NEED_IDENTIFIED: 'need_identified',
  AWAITING_APPROVALS: 'awaiting_approvals',
  APPROVED_ORDERED: 'approved_ordered',
};

const ResourceRequestsKanban: React.FC<ResourceRequestsKanbanProps> = ({
  onViewDetails,
}) => {
  const { exportAsForm } = useIcsFormPdfPreview();

  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedNeed, setSelectedNeed] = useState<any>(null);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const [selectedResourceKind, setSelectedResourceKind] =
    useState<string>('all');

  // Mutation for restoring cancelled requests
  const updateRequestMutation = useUpdateResourceRequest();

  // Fetch data
  const { data: requirements, isLoading: isLoadingReq } =
    useIncidentResourceRequirements();
  const { data: resourceSets, isLoading: isLoadingSets } = useResourceSets();
  const { data: resourceSites, isLoading: isLoadingSites } = useResourceSites();
  const { data: resourceRequests, isLoading: isLoadingRequests } =
    useIncidentResourceRequests(showCancelled);

  // Aggregate needs by resource kind
  const aggregatedNeeds = useMemo(
    () =>
      calculateAggregatedNeeds(
        requirements,
        resourceSets,
        resourceRequests,
        selectedResourceKind
      ),
    [requirements, resourceSets, resourceRequests, selectedResourceKind]
  );

  // Get unique resource kinds that have active needs or are in requests
  const availableResourceKinds = useMemo(
    () =>
      calculateAvailableResourceKinds(
        requirements,
        resourceSets,
        resourceRequests
      ),
    [requirements, resourceSets, resourceRequests]
  );

  const handleCreateRequest = useCallback((need: any) => {
    setSelectedNeed(need);
    setSelectedRequest(null);
    setShowDrawer(true);
  }, []);

  const handleViewEditRequest = useCallback((request: any) => {
    setSelectedRequest(request);
    setSelectedNeed(null);
    setShowDrawer(true);
  }, []);

  const handleRestoreRequest = useCallback(
    async (request: any) => {
      try {
        await updateRequestMutation.mutateAsync({
          requestId: request.resource_request_id,
          data: {
            status: 'draft',
            archived: false,
          },
        });
      } catch (error) {
        Sentry.captureException(error);
      }
    },
    [updateRequestMutation]
  );

  const handleDownloadAll213RR = () => {
    void exportAsForm({
      formname: 'ICS_213RR_CG',
      data: {},
      mapSketch: null,
    });
  };

  // Handle card moves between columns
  const handleCardMove = useCallback(
    (card: KanbanCard, source: KanbanColumn, destination: KanbanColumn) => {
      // Check if dragging from Need Identified to Awaiting Approvals
      if (
        source.id === COLUMN_IDS.NEED_IDENTIFIED &&
        destination.id === COLUMN_IDS.AWAITING_APPROVALS &&
        card.metadata?.cardType === 'need'
      ) {
        // Open the create request drawer with this need
        handleCreateRequest(card.metadata);
      }
      // Note: We don't actually move the card - the drawer will handle creating the request
      // which will update the data and refresh the board
    },
    [handleCreateRequest]
  );

  // Create kanban board data
  const board: KanbanBoard = useMemo(() => {
    const columns: KanbanColumn[] = [];

    // Add cancelled column if toggle is on
    if (showCancelled) {
      columns.push({
        id: COLUMN_IDS.CANCELLED,
        title: 'Cancelled',
        cards: filterRequestsByKind(
          (resourceRequests || []).filter(
            (request) => request.status === 'cancelled'
          ),
          selectedResourceKind
        ).map((request) => createRequestCard(request, 'cancelled')),
      });
    }

    // Regular columns
    columns.push(
      {
        id: COLUMN_IDS.NEED_IDENTIFIED,
        title: 'Need Identified',
        cards: aggregatedNeeds.map((need, index) => ({
          id: `need-${need.kind}-${index}`,
          title: need.kind,
          description: `Quantity needed: ${need.needed}`,
          metadata: {
            ...need,
            cardType: 'need',
          },
        })),
      },
      {
        id: COLUMN_IDS.AWAITING_APPROVALS,
        title: 'Awaiting Approvals',
        cards: filterRequestsByKind(
          (resourceRequests || []).filter(
            (request) =>
              request.status !== 'cancelled' &&
              [
                'draft',
                'awaiting_approval',
                'awaiting_review',
                'awaiting_order',
                'awaiting_payment',
              ].includes(request.status) &&
              // Exclude if it has finance signature (it's actually completed)
              !(
                request.status === 'awaiting_payment' &&
                request.finance_signature
              )
          ),
          selectedResourceKind
        ).map((request) => createRequestCard(request, 'request')),
      },
      {
        id: COLUMN_IDS.APPROVED_ORDERED,
        title: 'Approved & Ordered',
        cards: filterRequestsByKind(
          (resourceRequests || []).filter(
            (request) =>
              request.status !== 'cancelled' &&
              (request.status === 'completed' ||
                (request.status === 'awaiting_payment' &&
                  request.finance_signature))
          ),
          selectedResourceKind
        ).map((request) => createRequestCard(request, 'approved')),
      }
    );

    return { columns };
  }, [aggregatedNeeds, resourceRequests, showCancelled, selectedResourceKind]);

  // Custom card renderer
  const renderCard = (card: KanbanCard) => {
    return (
      <ResourceRequestKanbanCard
        card={card}
        onCreateRequest={handleCreateRequest}
        onViewEditRequest={handleViewEditRequest}
        onRestoreRequest={handleRestoreRequest}
        onViewDetails={onViewDetails}
        resourceRequests={resourceRequests}
        resourceSites={resourceSites}
        isRestoring={updateRequestMutation.isPending}
      />
    );
  };

  if (isLoadingReq || isLoadingSets || isLoadingSites || isLoadingRequests) {
    return <Loading />;
  }

  // Column styling
  const getColumnStyle = (columnId: string) => {
    const baseStyle = {
      borderTop: '4px solid',
    };

    switch (columnId) {
      case COLUMN_IDS.CANCELLED:
        return { ...baseStyle, borderTopColor: '#6c757d' };
      case COLUMN_IDS.NEED_IDENTIFIED:
        return { ...baseStyle, borderTopColor: '#dc3545' };
      case COLUMN_IDS.AWAITING_APPROVALS:
        return { ...baseStyle, borderTopColor: '#ffc107' };
      case COLUMN_IDS.APPROVED_ORDERED:
        return { ...baseStyle, borderTopColor: '#28a745' };
      default:
        return baseStyle;
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2">
          <StylishNewButton
            className="button--secondary"
            onClick={handleDownloadAll213RR}
          >
            <SharedIcon iconName="download" classes="me-2" />
            Preview All 213RR Forms
          </StylishNewButton>
        </div>
        <StylishNewButton
          className="button--primary"
          onClick={() => setShowDrawer(true)}
        >
          Add Resource Request
        </StylishNewButton>
      </div>

      <ResourceRequestKanbanFilters
        selectedResourceKind={selectedResourceKind}
        onResourceKindChange={setSelectedResourceKind}
        availableResourceKinds={availableResourceKinds}
        showCancelled={showCancelled}
        onShowCancelledChange={setShowCancelled}
      />
      <Kanban
        board={board}
        renderCard={renderCard}
        disableCardDrag={false}
        onCardMove={handleCardMove}
        columnStyle={getColumnStyle}
        height="calc(100vh - 250px)"
      />
      {showDrawer && (
        <ResourceRequestCreateDrawer
          toggle={() => {
            setShowDrawer(false);
            setSelectedNeed(null);
            setSelectedRequest(null);
          }}
          initialRequirement={selectedNeed}
          requestId={selectedRequest?.resource_request_id}
          initialRequest={selectedRequest}
        />
      )}
    </>
  );
};

export default ResourceRequestsKanban;
